import { useState } from "react";
import api, { formatIDR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { FileDown } from "lucide-react";

const monthStart = () => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0,10);
const today = () => new Date().toISOString().slice(0,10);

export default function Reports() {
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/reports/pnl?start=${start}&end=${end}`);
      setData(data);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="label-tag">Laporan</div>
        <h1 className="font-display text-4xl tracking-tight mt-1">Laba Rugi</h1>
        <p className="text-sm text-forest-400 mt-2">Ringkasan pendapatan, HPP dan PPN untuk kebutuhan pajak & audit internal.</p>
      </div>

      <div className="card-grid p-4 flex flex-wrap gap-3 items-end">
        <div><Label className="text-xs">Mulai</Label><Input data-testid="inp-start" type="date" value={start} onChange={e=>setStart(e.target.value)} className="rounded-sm"/></div>
        <div><Label className="text-xs">Sampai</Label><Input data-testid="inp-end" type="date" value={end} onChange={e=>setEnd(e.target.value)} className="rounded-sm"/></div>
        <Button data-testid="btn-run-report" onClick={run} disabled={busy} className="rounded-sm bg-[#2D4739] hover:bg-[#22362B]">{busy ? "Menghitung…" : "Jalankan"}</Button>
        {data && <Button variant="outline" onClick={()=>window.print()} className="rounded-sm gap-2"><FileDown size={16}/> Cetak PDF</Button>}
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="card-grid p-5"><div className="label-tag">Pendapatan</div><div className="font-display text-2xl num mt-2">{formatIDR(data.revenue)}</div></div>
            <div className="card-grid p-5"><div className="label-tag">HPP</div><div className="font-display text-2xl num mt-2">{formatIDR(data.cogs)}</div></div>
            <div className="card-grid p-5 border-l-4 border-l-[#2D4739]"><div className="label-tag">Laba Kotor</div><div className="font-display text-2xl num mt-2">{formatIDR(data.gross_profit)}</div></div>
            <div className="card-grid p-5 border-l-4 border-l-[#D99C3B]"><div className="label-tag">PPN Terhutang</div><div className="font-display text-2xl num mt-2">{formatIDR(data.tax_payable)}</div><div className="text-xs text-forest-400 mt-1">Out {formatIDR(data.ppn_out)} · In {formatIDR(data.ppn_in)}</div></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="card-grid p-6 lg:col-span-2">
              <div className="label-tag mb-4">Grafik Bulanan</div>
              <div className="h-72">
                <ResponsiveContainer>
                  <BarChart data={data.monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E6E8E6"/>
                    <XAxis dataKey="month" tick={{ fill:"#57605A", fontSize:12 }}/>
                    <YAxis tick={{ fill:"#57605A", fontSize:12 }}/>
                    <Tooltip contentStyle={{ background:"#FDFDFB", border:"1px solid #E6E8E6", borderRadius:4 }}/>
                    <Legend/>
                    <Bar dataKey="revenue" name="Pendapatan" fill="#2D4739"/>
                    <Bar dataKey="cogs" name="HPP" fill="#D99C3B"/>
                    <Bar dataKey="profit" name="Laba" fill="#4A6B53"/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="card-grid p-6">
              <div className="label-tag mb-4">Produk Terlaris</div>
              {data.top_products.length === 0 && <div className="text-sm text-forest-400">Belum ada penjualan.</div>}
              <div className="space-y-2">
                {data.top_products.map((p, i) => (
                  <div key={i} className="flex justify-between text-sm border-b border-forest-100 pb-2">
                    <div className="flex-1 truncate"><span className="font-mono text-xs text-forest-400 mr-2">#{i+1}</span>{p.name}</div>
                    <div className="text-right"><div className="num">{formatIDR(p.revenue)}</div><div className="text-xs text-forest-400 num">{p.qty} unit</div></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card-grid p-6">
            <div className="label-tag mb-3">Ringkasan</div>
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-forest-100"><td className="py-2 text-forest-400">Jumlah Invoice Terbit</td><td className="text-right num font-medium">{data.sales_count}</td></tr>
                <tr className="border-b border-forest-100"><td className="py-2 text-forest-400">Jumlah PO / Pembelian</td><td className="text-right num font-medium">{data.purchases_count}</td></tr>
                <tr className="border-b border-forest-100"><td className="py-2 text-forest-400">Pendapatan (DPP)</td><td className="text-right num">{formatIDR(data.revenue)}</td></tr>
                <tr className="border-b border-forest-100"><td className="py-2 text-forest-400">PPN Keluaran</td><td className="text-right num">{formatIDR(data.ppn_out)}</td></tr>
                <tr className="border-b border-forest-100"><td className="py-2 text-forest-400">HPP (DPP)</td><td className="text-right num">{formatIDR(data.cogs)}</td></tr>
                <tr className="border-b border-forest-100"><td className="py-2 text-forest-400">PPN Masukan</td><td className="text-right num">{formatIDR(data.ppn_in)}</td></tr>
                <tr className="border-b border-forest-100 font-display text-lg"><td className="py-2">Laba Kotor</td><td className="text-right num">{formatIDR(data.gross_profit)}</td></tr>
                <tr className="font-medium"><td className="py-2">PPN Terhutang (Setor)</td><td className="text-right num">{formatIDR(data.tax_payable)}</td></tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
